import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Smartphone, Apple, ShieldCheck, Zap, WifiOff, Bell, Check, ChevronDown, ChevronRight, Share2, Plus, MoreVertical, ArrowLeft, Send, Copy } from "lucide-react";

const SHARE_URL = "https://presupuestosrapidonline.com/instalar";
const SHARE_TEXT = "📲 Te paso esta herramienta que uso para hacer presupuestos en 30 segundos con IA. 14 días gratis y se instala en el móvil sin pasar por las tiendas:";

function ShareButton({ variant = "primary" }) {
  const [copied, setCopied] = useState(false);
  const onShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: "AutoPresupuestos Pro",
          text: SHARE_TEXT,
          url: SHARE_URL,
        });
        return;
      }
    } catch (e) {
      if (e?.name === "AbortError") return;
    }
    try {
      await navigator.clipboard.writeText(`${SHARE_TEXT}\n${SHARE_URL}`);
      setCopied(true);
      toast.success("Enlace copiado · pégalo en WhatsApp/Email");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("No se pudo copiar el enlace");
    }
  };
  const cls = variant === "primary"
    ? "h-12 px-6 bg-orange-600 text-white hover:bg-orange-700"
    : "h-11 px-4 border-2 border-slate-300 hover:border-orange-600 hover:text-orange-600";
  return (
    <button onClick={onShare} data-testid={`share-btn-${variant}`} className={`inline-flex items-center justify-center gap-2 font-bold text-sm ${cls}`}>
      {copied ? <Copy className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
      {copied ? "Copiado" : "Compartir con un autónomo"}
    </button>
  );
}

function ShareChannels() {
  const waMsg = encodeURIComponent(`${SHARE_TEXT} ${SHARE_URL}`);
  const subject = encodeURIComponent("Una herramienta que te va a gustar");
  const body = encodeURIComponent(`${SHARE_TEXT}\n\n${SHARE_URL}`);
  return (
    <div className="flex flex-wrap gap-2 mt-3" data-testid="share-channels">
      <a
        href={`https://wa.me/?text=${waMsg}`}
        target="_blank"
        rel="noreferrer"
        data-testid="share-whatsapp"
        className="h-11 px-4 inline-flex items-center gap-2 text-sm font-bold text-white"
        style={{ background: "#25D366" }}
      >
        <Send className="w-4 h-4" /> WhatsApp
      </a>
      <a
        href={`mailto:?subject=${subject}&body=${body}`}
        data-testid="share-email"
        className="h-11 px-4 inline-flex items-center gap-2 text-sm font-bold border-2 border-slate-300 hover:border-orange-600 hover:text-orange-600"
      >
        <Send className="w-4 h-4" /> Email
      </a>
      <ShareButton variant="secondary" />
    </div>
  );
}

const detectPlatform = () => {
  if (typeof navigator === "undefined") return "unknown";
  const ua = navigator.userAgent || "";
  if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
  if (/Android/i.test(ua)) return "android";
  return "desktop";
};

export default function Install() {
  const [platform, setPlatform] = useState("unknown");
  const [open, setOpen] = useState({ android: true, ios: true });

  useEffect(() => {
    const p = detectPlatform();
    setPlatform(p);
    // Auto-open the user's platform, collapse the other
    if (p === "android") setOpen({ android: true, ios: false });
    if (p === "ios") setOpen({ android: false, ios: true });
  }, []);

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="install-page">
      {/* Header */}
      <header className="border-b border-slate-200 sticky top-0 bg-white/95 backdrop-blur z-30">
        <div className="max-w-4xl mx-auto px-5 py-3 flex items-center justify-between gap-2">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900">
            <ArrowLeft className="w-4 h-4" /> Volver
          </Link>
          <div className="flex items-center gap-2">
            <ShareButton variant="secondary" />
            <Link to="/login" className="text-sm font-bold hover:text-orange-600 hidden sm:inline">Iniciar sesión</Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-4xl mx-auto px-5 py-12 sm:py-16">
        <div className="kbar text-orange-600">Instalar en tu móvil</div>
        <h1 className="font-display font-extrabold text-4xl sm:text-6xl tracking-tighter mt-3">
          AutoPresupuestos Pro<br/>
          <span className="text-orange-600">en tu pantalla de inicio</span>
        </h1>
        <p className="mt-5 text-lg text-slate-600 max-w-2xl">
          La app funciona como una aplicación nativa: icono, pantalla completa sin barra del navegador,
          notificaciones push y acceso sin conexión. Sin pasar por App Store ni Google Play.
        </p>

        {/* Benefits row */}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <Bullet icon={Zap} text="Se instala en 30 segundos" />
          <Bullet icon={ShieldCheck} text="Gratis · sin tienda" />
          <Bullet icon={Bell} text="Notificaciones push" />
          <Bullet icon={WifiOff} text="Funciona offline" />
        </div>

        {/* Share CTA — directly under benefits, super visible */}
        <div className="mt-10 bg-slate-900 text-white p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex-1">
            <div className="kbar text-orange-400">¿Conoces a otro autónomo?</div>
            <h2 className="font-display font-extrabold text-2xl mt-1">Compártesela en 1 toque</h2>
            <p className="text-slate-300 text-sm mt-1">Envía el link de instalación por WhatsApp o email.</p>
          </div>
          <ShareButton variant="primary" />
        </div>
      </section>

      {/* Platform detection notice */}
      {platform !== "unknown" && platform !== "desktop" && (
        <div className="max-w-4xl mx-auto px-5 -mt-4 mb-4">
          <div className="bg-orange-50 border border-orange-200 px-4 py-3 text-sm" data-testid="platform-hint">
            Hemos detectado que estás en{" "}
            <strong>{platform === "ios" ? "iPhone / iPad" : "Android"}</strong> — abajo tienes los pasos exactos.
          </div>
        </div>
      )}

      {/* Android */}
      <section className="max-w-4xl mx-auto px-5 pb-4">
        <button
          onClick={() => setOpen({ ...open, android: !open.android })}
          data-testid="toggle-android"
          className={`w-full flex items-center justify-between p-5 border-2 ${platform === "android" ? "border-orange-600 bg-orange-50" : "border-slate-200 bg-white"} hover:border-slate-400`}
        >
          <div className="flex items-center gap-3 text-left">
            <div className="h-12 w-12 bg-green-600 text-white flex items-center justify-center">
              <Smartphone className="w-6 h-6" />
            </div>
            <div>
              <div className="font-display font-extrabold text-xl">Android / Chrome</div>
              <div className="text-xs text-slate-500">~30 segundos · 3 pasos</div>
            </div>
          </div>
          {open.android ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
        </button>

        {open.android && (
          <div className="border-2 border-t-0 border-slate-200 p-5 sm:p-7 space-y-5" data-testid="android-steps">
            <Step
              n={1}
              title="Abre la app en Chrome"
              desc="En tu móvil Android, abre Google Chrome y entra en presupuestosrapidonline.com. Inicia sesión con tu cuenta."
            >
              <UrlChip url="presupuestosrapidonline.com" />
            </Step>

            <Step
              n={2}
              title="Pulsa el menú de Chrome"
              desc="Toca los tres puntos verticales ⋮ que aparecen arriba a la derecha. Se abrirá un menú con varias opciones."
            >
              <div className="inline-flex items-center gap-2 bg-slate-900 text-white px-4 py-3">
                <MoreVertical className="w-5 h-5" />
                <span className="text-sm">Menú Chrome (⋮ arriba derecha)</span>
              </div>
            </Step>

            <Step
              n={3}
              title='Pulsa "Instalar app" o "Añadir a pantalla de inicio"'
              desc="Confirma el nombre (AutoPresupuestos Pro) y pulsa Instalar. El icono naranja aparecerá en tu pantalla de inicio como cualquier otra app."
            >
              <div className="bg-white border border-slate-200 p-3 inline-flex items-center gap-2">
                <Plus className="w-4 h-4 text-green-600" />
                <span className="text-sm font-bold">Instalar app</span>
              </div>
            </Step>

            <Tip>
              💡 Muchos Android te muestran automáticamente un banner naranja "Instalar AutoPresupuestos Pro" cuando entras desde Chrome. Si lo ves, pulsa ese banner directamente.
            </Tip>
          </div>
        )}
      </section>

      {/* iOS */}
      <section className="max-w-4xl mx-auto px-5 pb-12">
        <button
          onClick={() => setOpen({ ...open, ios: !open.ios })}
          data-testid="toggle-ios"
          className={`w-full flex items-center justify-between p-5 border-2 ${platform === "ios" ? "border-orange-600 bg-orange-50" : "border-slate-200 bg-white"} hover:border-slate-400 mt-4`}
        >
          <div className="flex items-center gap-3 text-left">
            <div className="h-12 w-12 bg-slate-900 text-white flex items-center justify-center">
              <Apple className="w-6 h-6" />
            </div>
            <div>
              <div className="font-display font-extrabold text-xl">iPhone / iPad (Safari)</div>
              <div className="text-xs text-slate-500">~30 segundos · 3 pasos</div>
            </div>
          </div>
          {open.ios ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
        </button>

        {open.ios && (
          <div className="border-2 border-t-0 border-slate-200 p-5 sm:p-7 space-y-5" data-testid="ios-steps">
            <div className="bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-900">
              ⚠️ <strong>Importante:</strong> en iPhone tienes que usar <strong>Safari</strong> (el navegador azul nativo). Apple no permite instalar PWAs desde Chrome ni Firefox en iOS.
            </div>

            <Step
              n={1}
              title="Abre la app en Safari"
              desc="Abre Safari (no Chrome) en tu iPhone o iPad y entra en presupuestosrapidonline.com. Inicia sesión con tu cuenta."
            >
              <UrlChip url="presupuestosrapidonline.com" />
            </Step>

            <Step
              n={2}
              title="Toca el botón Compartir"
              desc="Es el icono cuadrado con una flecha hacia arriba ↑. Está abajo en el centro (iPhone) o arriba a la derecha (iPad)."
            >
              <div className="inline-flex items-center gap-2 bg-slate-900 text-white px-4 py-3">
                <Share2 className="w-5 h-5" />
                <span className="text-sm">Compartir (icono ↑)</span>
              </div>
            </Step>

            <Step
              n={3}
              title='Desliza y pulsa "Añadir a pantalla de inicio"'
              desc="Aparece una lista de opciones. Desliza hacia abajo, encuentra y pulsa Añadir a pantalla de inicio. Confirma el nombre y pulsa Añadir arriba a la derecha."
            >
              <div className="bg-white border border-slate-200 p-3 inline-flex items-center gap-2">
                <Plus className="w-4 h-4 text-slate-900" />
                <span className="text-sm font-bold">Añadir a pantalla de inicio</span>
              </div>
            </Step>

            <Tip>
              💡 Después de instalarla, ábrela siempre desde el icono de la pantalla de inicio (no desde Safari). Sólo así funciona en pantalla completa y guarda tu sesión correctamente.
            </Tip>
          </div>
        )}
      </section>

      {/* FAQ */}
      <section className="bg-slate-50 border-t border-slate-200 py-12">
        <div className="max-w-4xl mx-auto px-5">
          <h2 className="font-display font-extrabold text-3xl tracking-tight">Preguntas frecuentes</h2>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
            <Faq q="¿Por qué no está en Google Play / App Store?">
              Porque no hace falta. Es una PWA: tecnología que permite instalar la web como app nativa,
              sin las tiendas (que cuestan dinero y se quedan comisión). El usuario tiene la misma experiencia
              que con una app descargada, pero sin esperas ni revisiones.
            </Faq>
            <Faq q="¿Ocupa mucho espacio?">
              Menos de 5 MB. Mucho menos que una app de Play Store o App Store (que suelen ser 50-200 MB).
            </Faq>
            <Faq q="¿Funciona sin internet?">
              Las pantallas básicas (dashboard, presupuestos guardados, clientes) sí. Para generar
              presupuestos con IA o sincronizar cambios necesitas conexión.
            </Faq>
            <Faq q="¿Recibiré notificaciones push?">
              Sí, si las activas desde Ajustes → Notificaciones. Te avisamos cuando un cliente acepta
              un presupuesto, cuando vence una factura, y de tu prueba gratuita.
            </Faq>
            <Faq q="¿Y si actualizáis la app?">
              Se actualiza sola cuando la abres. No tienes que descargar nada nuevo nunca.
            </Faq>
            <Faq q="¿Es seguro?">
              Más seguro que una app tradicional: corre sobre HTTPS con certificado válido, tus datos van
              cifrados y nunca instalas archivos sospechosos.
            </Faq>
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="max-w-4xl mx-auto px-5 py-12">
        <div className="bg-orange-50 border-2 border-orange-200 p-6 sm:p-8 text-center">
          <h3 className="font-display font-extrabold text-2xl">Ayúdanos a llegar a más autónomos</h3>
          <p className="text-slate-600 mt-2 text-sm max-w-md mx-auto">Si te ha sido útil, mándale el link a algún compañero o cliente. Tarda 5 segundos.</p>
          <div className="mt-5 flex flex-wrap gap-2 justify-center">
            <ShareChannels />
          </div>
        </div>

        <div className="mt-12 text-center">
          <h3 className="font-display font-extrabold text-2xl">¿Aún no tienes cuenta?</h3>
          <p className="text-slate-500 mt-2 text-sm">Empieza con 14 días gratis. Sin tarjeta. Cancela cuando quieras.</p>
          <Link to="/register" data-testid="install-register-cta" className="mt-5 inline-flex items-center justify-center h-12 px-6 bg-orange-600 text-white font-bold hover:bg-orange-700">
            Crear cuenta gratis →
          </Link>
        </div>
      </section>
    </div>
  );
}

function Bullet({ icon: Icon, text }) {
  return (
    <div className="flex items-center gap-2 text-slate-700">
      <Icon className="w-4 h-4 text-orange-600 flex-shrink-0" />
      <span>{text}</span>
    </div>
  );
}

function Step({ n, title, desc, children }) {
  return (
    <div className="flex gap-4">
      <div className="flex-shrink-0 h-10 w-10 bg-slate-900 text-white flex items-center justify-center font-display font-extrabold text-lg">{n}</div>
      <div className="flex-1">
        <h3 className="font-display font-bold text-lg">{title}</h3>
        <p className="text-sm text-slate-600 mt-1">{desc}</p>
        {children && <div className="mt-3">{children}</div>}
      </div>
    </div>
  );
}

function UrlChip({ url }) {
  return (
    <div className="inline-flex items-center bg-slate-900 text-white">
      <div className="px-3 py-2 bg-slate-700 text-xs">https://</div>
      <div className="px-3 py-2 text-sm font-mono">{url}</div>
    </div>
  );
}

function Tip({ children }) {
  return <div className="bg-orange-50 border-l-4 border-orange-600 px-4 py-3 text-sm text-slate-700">{children}</div>;
}

function Faq({ q, children }) {
  const [open, setOpen] = useState(false);
  return (
    <button onClick={() => setOpen(!open)} className="bg-white border border-slate-200 p-4 text-left hover:border-slate-400 block w-full">
      <div className="flex items-center justify-between gap-3">
        <span className="font-bold text-sm">{q}</span>
        <ChevronDown className={`w-4 h-4 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </div>
      {open && <div className="mt-3 text-sm text-slate-600">{children}</div>}
    </button>
  );
}
